import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';

import { Recent } from '@common/models';

/**
 * Recientes: las últimas pantallas que visitó el usuario.
 *
 * No hay backend detrás — todo se guarda en el localStorage del navegador con
 * una clave por usuario y se conservan solo los últimos {MAX} elementos, del
 * más reciente al más viejo.
 */
@Injectable({ providedIn: 'root' })
export class RecentService {

  /** Cantidad máxima de recientes que se guardan */
  static readonly MAX = 9;

  /** Emite la lista actualizada cada vez que se registra una visita */
  public onRecentsChanged: Subject<Recent[]> = new Subject<Recent[]>();

  /**
   * Devuelve los recientes del usuario, del más nuevo al más viejo
   * @param userId Usuario dueño de la lista (username o email)
   */
  findByUserId(userId: string): Recent[] {
    try {
      const raw = localStorage.getItem(this._key(userId));
      if (!raw) return [];

      const list = JSON.parse(raw);
      if (!Array.isArray(list)) return [];

      return list
        .filter((r: Recent) => !!r?.url)
        .sort((a: Recent, b: Recent) => this._time(b) - this._time(a))
        .slice(0, RecentService.MAX);
    } catch {
      // localStorage lleno/bloqueado o json corrupto: se trata como vacío
      return [];
    }
  }

  /**
   * Registra una visita. Si la pantalla ya estaba en la lista solo se actualiza
   * su fecha (no se duplica) y pasa al principio.
   * @param userId Usuario dueño de la lista
   * @param item Pantalla visitada (requiere url)
   * @returns La lista resultante
   */
  add(userId: string, item: Recent): Recent[] {
    if (!userId || !item?.url) return this.findByUserId(userId);

    const rest = this.findByUserId(userId).filter(r => r.url !== item.url);
    const list = [{ ...item, visitedAt: new Date().toISOString() }, ...rest]
      .slice(0, RecentService.MAX);

    try {
      localStorage.setItem(this._key(userId), JSON.stringify(list));
    } catch {
      // Si no se pudo guardar, igual se devuelve la lista en memoria
    }

    this.onRecentsChanged.next(list);
    return list;
  }

  /**
   * Borra los recientes del usuario
   * @param userId Usuario dueño de la lista
   */
  clear(userId: string): void {
    try {
      localStorage.removeItem(this._key(userId));
    } catch {
      // nada que hacer
    }
    this.onRecentsChanged.next([]);
  }

  /** Clave de localStorage del usuario */
  private _key(userId: string): string {
    return `beliv-recents:${userId || 'anon'}`;
  }

  /** Fecha de visita en milisegundos (0 si no trae fecha) */
  private _time(r: Recent): number {
    const t = new Date(r?.visitedAt || 0).getTime();
    return isNaN(t) ? 0 : t;
  }
}
